import { useRef } from 'react';
import { useFramework, useIdentity, useMoveEventListener, useScale } from '@/framework';
import type { RulerBar } from '@/plugins/apps/user-editor/ruler-bar';
import { REFERENCE_LINE_DIRECTION } from '../constants';
import { AddReferenceLine } from '../operations/add-reference-line';
import { DeleteReferenceLine } from '../operations/delete-reference-line';
import { UpdateReferenceLine } from '../operations/update-reference-line';
import { useReferenceLinesState } from '../states/reference-lines-state';
import type { ReferenceLine } from '../types';

// 从标尺处拖动添加参考线
export function useAddReferenceLineFromRuler(paddingLeft: number, paddingTop: number) {
    const identity = useIdentity();
    const framework = useFramework();
    const scale = useScale();
    const rulerBar = framework.service.registration.getInstance<RulerBar>('RulerBar');

    const minPosition = useRef(Infinity);
    const rulerBarSize = useRef(0);
    const isDragging = useRef(false);
    const draggingReferenceLine = useRef<ReferenceLine | null>(null);

    const addReferenceLine = new AddReferenceLine(identity);
    const deleteReferenceLine = new DeleteReferenceLine(identity);
    const updateReferenceLine = new UpdateReferenceLine(identity);

    const referenceLinesState = useReferenceLinesState();
    const setDraggingLineId = referenceLinesState((state) => state.setDraggingLineId);
    const setTooltipPosition = referenceLinesState((state) => state.setTooltipPosition);
    const setDraggingLine = referenceLinesState((state) => state.setDraggingLine);
    const setRulerBarSize = referenceLinesState((state) => state.setRulerBarSize);

    // 拖动事件
    useMoveEventListener((e) => {
        // 点击的是标尺
        const isOnRuler = e.target.classList.contains('l-ruler-bar__canvas');
        if (!isOnRuler) return;

        const isHorizontal = e.target.classList.contains('horizontal-ruler');

        // 拖拽开始
        if (e.isStart) {
            const rect = e.target.getBoundingClientRect();
            minPosition.current = isHorizontal ? rect.top + rect.height : rect.left + rect.width;
            isDragging.current = true;
            rulerBarSize.current = rulerBar?.getVisibleRulerBarSize() ?? 0;
            setRulerBarSize(rulerBarSize.current);
            framework.service.editor.setDisablePick(true);
            framework.service.frame.delActiveLayer();
        }
        // 拖拽结束
        else if (e.isEnd) {
            if (draggingReferenceLine.current) {
                const { direction, position, id } = draggingReferenceLine.current;
                // 为了避免产生历史记录, 需要删除添加过程中的临时参考线后, 再重新添加一条
                deleteReferenceLine.delete(id, false);

                // 只有拖动距离超过标尺, 才添加参考线
                const lastPosition = isHorizontal ? e.currentPoint.y : e.currentPoint.x;
                if (lastPosition > minPosition.current) {
                    // 添加一条带历史记录的参考线
                    addReferenceLine.addWithLineData({
                        direction,
                        position,
                    });
                }
            }

            setDraggingLineId('');
            setDraggingLine(null);
            draggingReferenceLine.current = null;
            minPosition.current = Infinity;
            rulerBarSize.current = 0;
            isDragging.current = false;
            framework.service.editor.setDisablePick(false);
        }
        // 拖拽中
        else {
            if (!isDragging.current) return;

            const position = isHorizontal
                ? (e.currentPoint.y - minPosition.current - paddingTop + rulerBarSize.current) / scale
                : (e.currentPoint.x - minPosition.current - paddingLeft + rulerBarSize.current) / scale;

            // 设置 tooltip 的位置
            setTooltipPosition({
                x: e.currentPoint.x,
                y: e.currentPoint.y,
            });

            framework.service.frame.setHoverId('');

            // 已经添加参考线, 则更新参考线位置
            if (draggingReferenceLine.current) {
                const newLine = {
                    ...draggingReferenceLine.current,
                    position,
                };
                updateReferenceLine.update(newLine, undefined, false);
                setDraggingLine(newLine);
                draggingReferenceLine.current.position = position;
            }
            // 还没添加参考线, 线判断看看是否达到添加参考线的条件
            else {
                const isOverMinPosition = isHorizontal
                    ? e.currentPoint.y >= minPosition.current
                    : e.currentPoint.x >= minPosition.current;

                // 达到距离(拖动超过标尺)就添加参考线
                if (isOverMinPosition) {
                    const direction = isHorizontal
                        ? REFERENCE_LINE_DIRECTION.HORIZONTAL
                        : REFERENCE_LINE_DIRECTION.VERTICAL;

                    const result = addReferenceLine.addWithLineData(
                        {
                            direction,
                            position,
                        },
                        false,
                    );

                    setDraggingLineId(result.id);
                    setDraggingLine(result);
                    draggingReferenceLine.current = result;
                }
            }
        }
    });
}
